import React, { useState, useEffect, useCallback, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Chip from "@material-ui/core/Chip";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll", overflowX: "auto",
    ...theme.scrollbarStyles
  },
  cellText: {
    maxWidth: 320,
    whiteSpace: "pre-wrap"
  }
}));

const STATUS_TABS = ["pending", "approved", "rejected"];

const STATUS_LABEL = {
  pending: "Pendentes",
  approved: "Aprovadas",
  rejected: "Rejeitadas"
};

// FAQs importadas em lote (base de conhecimento do parceiro, passo a
// passos internos, etc) vem com um prefixo "[Sistema] " na pergunta pra
// indicar de onde vieram - usado aqui só pra montar o filtro "Sistema",
// não é um campo separado no banco.
const extractSystemTag = question => {
  const match = /^\[([^\]]+)\]/.exec(question || "");
  return match ? match[1] : null;
};

const FaqModeration = ({ embedded = false }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswer, setEditAnswer] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [systemFilter, setSystemFilter] = useState("Todos");

  const status = STATUS_TABS[tab];

  const systemOptions = useMemo(() => {
    const tags = new Set();
    faqs.forEach(faq => {
      const tag = extractSystemTag(faq.question);
      if (tag) tags.add(tag);
    });
    return ["Todos", ...Array.from(tags).sort()];
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return faqs.filter(faq => {
      if (systemFilter !== "Todos" && extractSystemTag(faq.question) !== systemFilter) return false;
      if (!term) return true;
      return (
        faq.question.toLowerCase().includes(term) || faq.answer.toLowerCase().includes(term)
      );
    });
  }, [faqs, searchTerm, systemFilter]);

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/faqs", { params: { status } });
      setFaqs(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const handleChangeStatus = async (faq, newStatus) => {
    try {
      await api.put(`/faqs/${faq.id}`, { status: newStatus });
      toast.success(
        newStatus === "approved" ? "FAQ aprovada, já pode ser usada pelo assistente." : "FAQ rejeitada."
      );
      setFaqs(prev => prev.filter(f => f.id !== faq.id));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenEdit = faq => {
    setEditingFaq(faq);
    setEditQuestion(faq.question);
    setEditAnswer(faq.answer);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/faqs/${editingFaq.id}`, { question: editQuestion, answer: editAnswer });
      toast.success("FAQ atualizada.");
      setFaqs(prev =>
        prev.map(f => (f.id === editingFaq.id ? { ...f, question: editQuestion, answer: editAnswer } : f))
      );
      setEditingFaq(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleApproveFromEdit = async () => {
    try {
      await api.put(`/faqs/${editingFaq.id}`, {
        question: editQuestion,
        answer: editAnswer,
        status: "approved"
      });
      toast.success("FAQ editada e aprovada.");
      setFaqs(prev => prev.filter(f => f.id !== editingFaq.id));
      setEditingFaq(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/faqs/${confirmDelete.id}`);
      toast.success("FAQ excluída.");
      setFaqs(prev => prev.filter(f => f.id !== confirmDelete.id));
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDelete(null);
    }
  };

  const content = (
    <>
      <ConfirmationModal
        title="Excluir FAQ?"
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      >
        Essa FAQ vai ser removida permanentemente.
      </ConfirmationModal>

      <Dialog open={!!editingFaq} onClose={() => setEditingFaq(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar FAQ</DialogTitle>
        <DialogContent>
          <TextField
            label="Pergunta"
            fullWidth
            multiline
            variant="outlined"
            margin="dense"
            value={editQuestion}
            onChange={e => setEditQuestion(e.target.value)}
          />
          <TextField
            label="Resposta"
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            margin="dense"
            value={editAnswer}
            onChange={e => setEditAnswer(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingFaq(null)}>Cancelar</Button>
          <Button onClick={handleSaveEdit} color="primary">
            Salvar
          </Button>
          {editingFaq && editingFaq.status !== "approved" && (
            <Button onClick={handleApproveFromEdit} color="primary" variant="contained">
              Salvar e aprovar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {!embedded && (
        <MainHeader>
          <Title>Base de Conhecimento (IA)</Title>
        </MainHeader>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
        {STATUS_TABS.map(s => (
          <Tab key={s} label={STATUS_LABEL[s]} />
        ))}
      </Tabs>

      <div style={{ padding: 8, fontSize: 13, color: "#666" }}>
        Candidatos são extraídos automaticamente dos atendimentos reais quando um ticket é fechado. Só FAQs
        aprovadas aqui entram na base que o assistente de IA usa pra responder dúvidas de suporte.
      </div>

      <Grid container spacing={2} style={{ padding: "0 8px 8px" }}>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar por palavra-chave na pergunta ou resposta..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            label="Sistema/Assunto"
            value={systemFilter}
            onChange={e => setSystemFilter(e.target.value)}
          >
            {systemOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <div className={classes.mainPaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pergunta</TableCell>
              <TableCell>Resposta</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={3} />
            ) : (
              filteredFaqs.map(faq => (
                <TableRow key={faq.id}>
                  <TableCell className={classes.cellText}>{faq.question}</TableCell>
                  <TableCell className={classes.cellText}>{faq.answer}</TableCell>
                  <TableCell align="center">
                    {status === "pending" && (
                      <>
                        <IconButton
                          size="small"
                          title="Aprovar"
                          onClick={() => handleChangeStatus(faq, "approved")}
                        >
                          <CheckCircleOutlineIcon style={{ color: "#2e7d32" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Rejeitar"
                          onClick={() => handleChangeStatus(faq, "rejected")}
                        >
                          <HighlightOffIcon style={{ color: "#c62828" }} />
                        </IconButton>
                      </>
                    )}
                    <IconButton size="small" title="Editar" onClick={() => handleOpenEdit(faq)}>
                      <EditIcon />
                    </IconButton>
                    {status !== "pending" && (
                      <IconButton size="small" title="Excluir" onClick={() => setConfirmDelete(faq)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
            {!loading && filteredFaqs.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Chip
                    label={
                      faqs.length === 0
                        ? `Nenhuma FAQ ${STATUS_LABEL[status].toLowerCase()}`
                        : "Nenhuma FAQ encontrada com esse filtro"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );

  return embedded ? content : <MainContainer>{content}</MainContainer>;
};

export default FaqModeration;
